/**
 * AVD Provisioning TypeScript Models
 * Aligned with backend C# models for VDI ticket to AVD provisioning flow
 */

/**
 * VDI Ticket Submission
 */
export interface VdiTicketSubmission {
  ticketId: string;
  requesterEmail: string;
  requesterDisplayName?: string;
  scheduleStartUtc: string; // ISO 8601 format
  scheduleEndUtc: string; // ISO 8601 format
  isRepeated: boolean;
  repeatUntilUtc?: string; // ISO 8601 format
  serviceDescription?: string;
  softwareSet: string;
  softwareList?: string; // JSON array as string
  gpuRequirement: boolean;
  justification: string;
  remarks?: string;
  sourceSystem?: string;
  createdUtc: string; // ISO 8601 format
}

/**
 * AVD Provisioning Status
 */
export interface AvdProvisioningStatus {
  provisioningAttemptId: string;
  ticketId: string;
  status: 'Pending' | 'InProgress' | 'Success' | 'Failed';
  hostName?: string;
  hostPoolId?: string;
  azureResourceId?: string;
  attemptNumber: number;
  requestedVmSize?: string;
  requestedImage?: string;
  startedUtc: string; // ISO 8601 format
  completedUtc?: string; // ISO 8601 format
  failureCode?: string;
  failureMessage?: string;
  correlationId: string;
}

/**
 * AVD Connection Details
 */
export interface AvdConnectionDetails {
  connectionDetailsId: string;
  provisioningAttemptId: string;
  ticketId: string;
  connectionLink?: string;
  connectionFileUri?: string;
  temporaryUsername?: string; // Masked unless authorized
  temporaryPassword?: string; // Masked unless authorized
  credentialExpiresUtc: string; // ISO 8601 format
  createdUtc: string; // ISO 8601 format
}

/**
 * Ticket Provisioning Submit Request
 */
export interface VdiTicketSubmitRequest {
  requesterEmail: string;
  requesterDisplayName?: string;
  scheduleStartUtc: Date | string;
  scheduleEndUtc: Date | string;
  isRepeated: boolean;
  repeatUntilUtc?: Date | string;
  serviceDescription?: string;
  softwareSet: string;
  softwareList?: string;
  gpuRequirement: boolean;
  justification: string;
  remarks?: string;
}

/**
 * Ticket Provisioning Submit Response
 */
export interface VdiTicketSubmitResponse {
  ticketId: string;
  ticketNumber: string;
  message: string;
  statusCheckUrl: string;
  submittedAt?: string;
  provisioningStatus: 'Pending' | 'InProgress' | 'Success' | 'Failed' | 'AutoApprovalFailed';
}

/**
 * Provisioning Status Response
 */
export interface ProvisioningStatusResponse {
  ticketId: string;
  status: 'Pending' | 'InProgress' | 'Success' | 'Failed';
  attemptNumber: number;
  vmSize?: string;
  hostName?: string;
  startedUtc: string;
  completedUtc?: string;
  failureCode?: string;
  failureMessage?: string;
  hasConnectionDetails: boolean;
}

/**
 * Connection Details Response
 */
export interface ConnectionDetailsResponse {
  ticketId: string;
  connectionLink?: string;
  connectionFileUri?: string;
  temporaryUsername?: string;
  temporaryPassword?: string;
  credentialExpiresUtc: string;
  daysUntilExpiry: number;
}

/**
 * Retry Provisioning Response
 */
export interface RetryProvisioningResponse {
  ticketId: string;
  attemptNumber: number;
  message: string;
  statusCheckUrl: string;
  retriedAt: string;
}

/**
 * Error Response
 */
export interface ProvisioningErrorResponse {
  error: string;
  message: string;
  action?: string;
  currentStatus?: string;
  expiredAt?: string;
}

/**
 * Status Badge Configuration
 */
export interface StatusBadgeConfig {
  text: string;
  color: 'default' | 'processing' | 'success' | 'error' | 'warning';
  icon?: string;
}

/**
 * Helper function to get status badge configuration
 */
export function getStatusBadge(status: string): StatusBadgeConfig {
  switch (status) {
    case 'Pending':
      return { text: 'Pending', color: 'default', icon: 'clock-circle' };
    case 'InProgress':
      return { text: 'In Progress', color: 'processing', icon: 'loading' };
    case 'Success':
      return { text: 'Success', color: 'success', icon: 'check-circle' };
    case 'Failed':
      return { text: 'Failed', color: 'error', icon: 'close-circle' };
    default:
      return { text: status, color: 'default' };
  }
}

/**
 * Helper function to format date for display
 */
export function formatProvisioningDate(dateString: string | undefined): string {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleString('en-US', { timeZone: 'Asia/Hong_Kong' });
}

/**
 * Helper function to check if credentials are expired
 */
export function areCredentialsExpired(expiryDateString: string): boolean {
  const expiryDate = new Date(expiryDateString);
  return new Date() > expiryDate;
}

/**
 * Helper function to calculate days until expiry
 */
export function daysUntilExpiry(expiryDateString: string): number {
  const expiryDate = new Date(expiryDateString);
  const now = new Date();
  const diffTime = expiryDate.getTime() - now.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return Math.max(0, diffDays);
}























